import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLegalDocument } from '../../hooks/useLegalDocument.js';

function formatSnapshot(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-US', {
    dateStyle: 'long',
    timeStyle: 'short'
  });
}

export default function LegalModal({ docType, title, onClose }) {
  const { content, loading, error, refetch, updatedAt } = useLegalDocument(docType);
  const formattedTimestamp = formatSnapshot(updatedAt);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [handleKeyDown]);

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!docType) return null;

  return createPortal(
    <div
      className="legal-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={handleOverlayClick}
    >
      <div className="legal-modal">
        <header className="legal-modal__header">
          <button type="button" className="legal-modal__close" onClick={onClose}>
            Close
          </button>
        </header>

        <div className="legal-modal__body">
          {loading && (
            <div className="legal-status" role="status">
              Loading {title}…
            </div>
          )}

          {error && (
            <div className="legal-status legal-status--error" role="alert">
              <p>{error}</p>
              <button type="button" className="legal-refresh-button" onClick={refetch}>
                Try again
              </button>
            </div>
          )}

          {!loading && !error && content && (
            <>
              <div className="legal-content" dangerouslySetInnerHTML={{ __html: content }} />
              {formattedTimestamp && (
                <p className="legal-timestamp">Snapshot updated {formattedTimestamp}</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
